import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Circle, Clock, Download, Loader2, PartyPopper, RefreshCw, XCircle } from 'lucide-react';
import ApplicationShell from '../../components/application/ApplicationShell';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/ToastProvider';
import driverApplicationService, { errorMessage } from '../../services/driverApplicationService';
import useLeaveIfNotApplicant from './useLeaveIfNotApplicant';
import { STEP_LABELS } from '../../components/application/stepConfig';
import { formatDisplayDateTime, formatPhoneInput } from '../../utils/dateInput';

const TimelineItem = ({ state, title, detail, last = false }) => {
  const Icon = state === 'done' ? CheckCircle2 : state === 'current' ? Clock : state === 'bad' ? XCircle : Circle;
  const color = state === 'done' ? 'text-green-600' : state === 'current' ? 'text-amber-500' : state === 'bad' ? 'text-red-500' : 'text-gray-300';
  return (
    <li className="relative flex gap-3 pb-6">
      {!last && <span className="absolute left-[11px] top-6 h-full w-0.5 bg-gray-200" aria-hidden="true" />}
      <Icon className={`relative z-10 mt-0.5 h-6 w-6 flex-shrink-0 bg-white ${color}`} aria-hidden="true" />
      <div>
        <p className={`text-sm font-medium ${state === 'upcoming' ? 'text-gray-500' : 'text-gray-900'}`}>{title}</p>
        {detail && <p className="mt-0.5 text-xs text-gray-500">{detail}</p>}
      </div>
    </li>
  );
};

const ApplyStatusPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { refreshUserProfile } = useAuth();
  const leaveIfNotApplicant = useLeaveIfNotApplicant();
  const [bundle, setBundle] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await driverApplicationService.getMine();
      setBundle(data);
      if (['in_progress', 'changes_requested'].includes(data.application.status) && !data.application.submittedAt) {
        navigate('/apply', { replace: true });
      }
    } catch (err) {
      if (await leaveIfNotApplicant(err)) return;
      setError(errorMessage(err, 'We could not load your application.'));
    } finally {
      setLoading(false);
    }
  }, [navigate, leaveIfNotApplicant]);

  useEffect(() => { load(); }, [load]);

  // Once hired, the user's role flips to driver; refresh the profile so routing sends them to the portal.
  useEffect(() => {
    if (bundle?.application?.status === 'hired') {
      const timer = setTimeout(async () => {
        const result = await refreshUserProfile();
        if (result?.user?.role === 'driver') navigate('/driver-portal', { replace: true });
      }, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [bundle, refreshUserProfile, navigate]);

  const handleDownload = async () => {
    if (!bundle) return;
    setDownloading(true);
    try {
      const { url } = await driverApplicationService.getPdfUrl(bundle.application.id, 'submitted');
      // The signed URL is served as an attachment, so navigating to it downloads
      // the file and keeps this page. (window.open after an await is blocked by Safari.)
      window.location.assign(url);
    } catch (err) {
      toast.error(errorMessage(err, 'The PDF is not ready yet. Try again in a minute.'));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <ApplicationShell subtitle="Application status">
        <div className="flex flex-col items-center py-20 text-gray-500">
          <Loader2 className="h-8 w-8 animate-spin text-brand-mid" aria-hidden="true" />
          <p className="mt-3 text-sm">Loading your application…</p>
        </div>
      </ApplicationShell>
    );
  }

  if (error || !bundle) {
    return (
      <ApplicationShell subtitle="Application status">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error || 'No application found.'}</div>
        <button type="button" onClick={load} className="btn-primary mt-4">Try again</button>
      </ApplicationShell>
    );
  }

  const { application, company } = bundle;
  const status = application.status;
  const submittedAt = application.submittedAt ? formatDisplayDateTime(application.submittedAt) : null;
  const decidedAt = application.review?.decidedAt ? formatDisplayDateTime(application.review.decidedAt) : null;

  if (status === 'withdrawn' || status === 'expired') {
    return (
      <ApplicationShell companyName={company.name} subtitle="Application status">
        <div className="mx-auto max-w-2xl rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-gray-900">
            {status === 'expired' ? 'This invitation has expired' : 'This application is closed'}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            {status === 'expired'
              ? `Your invitation to apply to ${company.name} expired before the application was submitted.`
              : `${company.name} has withdrawn this application invitation.`}
            {' '}If you still want to apply, contact {company.name}{company.phone ? ` at ${formatPhoneInput(company.phone)}` : ''}{company.email ? ` or ${company.email}` : ''} and ask for a new invitation.
          </p>
        </div>
      </ApplicationShell>
    );
  }

  const timeline = [
    { state: 'done', title: 'Application submitted', detail: submittedAt },
    status === 'changes_requested'
      ? { state: 'current', title: 'Updates requested', detail: 'See the note below and update your application.' }
      : { state: ['approved', 'hired', 'rejected'].includes(status) ? 'done' : 'current', title: 'Under review', detail: ['approved', 'hired', 'rejected'].includes(status) ? `Reviewed ${decidedAt || ''}` : `${company.name} is reviewing your application and documents.` },
    status === 'rejected'
      ? { state: 'bad', title: 'Not selected', detail: decidedAt }
      : { state: ['approved', 'hired'].includes(status) ? 'done' : 'upcoming', title: 'Approved', detail: ['approved', 'hired'].includes(status) ? decidedAt : 'You will get an email when a decision is made.' },
    { state: status === 'hired' ? 'done' : 'upcoming', title: 'Hired and driver portal opened', detail: status === 'hired' ? 'Welcome aboard!' : null }
  ];

  return (
    <ApplicationShell companyName={company.name} subtitle="Application status">
      <div className="mx-auto max-w-2xl space-y-6">
        {status === 'changes_requested' && (
          <div role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-amber-900">{company.name} asked for a few updates</h2>
                <p className="mt-1 whitespace-pre-line text-sm text-amber-900">{application.reviewerMessage}</p>
                {application.flaggedSteps?.length > 0 && (
                  <p className="mt-2 text-xs text-amber-800">Sections to update: {application.flaggedSteps.map((s) => STEP_LABELS[s] || s).join(', ')}</p>
                )}
                <button type="button" onClick={() => navigate('/apply')} className="btn-primary mt-4">Update my application</button>
              </div>
            </div>
          </div>
        )}

        {status === 'approved' && (
          <div className="rounded-xl border border-green-200 bg-green-50 p-5">
            <div className="flex items-start gap-3">
              <PartyPopper className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" aria-hidden="true" />
              <div>
                <h2 className="text-base font-semibold text-green-900">Your application was approved</h2>
                <p className="mt-1 text-sm text-green-900">{company.name} will contact you about next steps, which may include a pre-employment drug test, a road test, and your start date.</p>
              </div>
            </div>
          </div>
        )}

        {status === 'hired' && (
          <div className="rounded-xl border border-green-200 bg-green-50 p-5 text-center">
            <PartyPopper className="mx-auto h-8 w-8 text-green-600" aria-hidden="true" />
            <h2 className="mt-2 text-lg font-semibold text-green-900">Welcome to {company.name}!</h2>
            <p className="mt-1 text-sm text-green-900">Opening your driver portal…</p>
            <button type="button" onClick={async () => { await refreshUserProfile(); navigate('/driver-portal'); }} className="btn-primary mt-4">Go to my driver portal</button>
          </div>
        )}

        {status === 'rejected' && (
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h2 className="text-base font-semibold text-gray-900">Thank you for applying</h2>
            <p className="mt-1 text-sm text-gray-600">{company.name} has decided not to move forward at this time. We appreciate the time you took to apply.</p>
            {application.review?.reason && (
              <p className="mt-3 whitespace-pre-line rounded-lg bg-gray-50 p-3 text-sm text-gray-700">{application.review.reason}</p>
            )}
          </div>
        )}

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold text-gray-900">Your application</h1>
            <button type="button" onClick={load} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800" aria-label="Refresh status">
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> Refresh
            </button>
          </div>
          <ol className="mt-5">
            {timeline.map((item, index) => (
              <TimelineItem key={item.title} {...item} last={index === timeline.length - 1} />
            ))}
          </ol>
          {application.submittedAt && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
              Download my signed application (PDF)
            </button>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-600 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900">Questions?</h2>
          <p className="mt-1">Contact {company.name}{company.phone ? ` at ${formatPhoneInput(company.phone)}` : ''}{company.email ? ` or ${company.email}` : ''}.</p>
        </section>
      </div>
    </ApplicationShell>
  );
};

export default ApplyStatusPage;
